/**
 * BackBar — a unified breadcrumb + Back affordance for every detail page.
 *
 * Shape:
 *   ┌─ Back ─┐   Section › Parent › Current page label
 *   └────────┘   └─── hairline-divided link trail ──────┘
 *
 * The Back chip uses real in-app history (navigate(-1)) when there is any,
 * and falls back to the page's declared `parent` route otherwise — so a
 * direct deep-link from a fresh tab still gets a sensible destination.
 *
 * In-app history depth is tracked via the `useTrackHistoryDepth()` hook
 * that lives in Shell — it bumps `sessionStorage.wn.histDepth` on every
 * route change. Without that bump the BackBar treats the visit as
 * deep-linked and shows the parent-route fallback.
 */
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ChevronRight } from "lucide-react";

const DEPTH_KEY = "wn.histDepth";

export function getHistoryDepth() {
  try {
    const n = Number(sessionStorage.getItem(DEPTH_KEY) || 0);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

export function bumpHistoryDepth() {
  try {
    sessionStorage.setItem(DEPTH_KEY, String(getHistoryDepth() + 1));
  } catch {
    /* ignore quota errors */
  }
}

export function resetHistoryDepth() {
  try {
    sessionStorage.removeItem(DEPTH_KEY);
  } catch {
    /* ignore quota errors */
  }
}

/**
 * <BackBar
 *   parent={{ to: "/owner/projects", label: "Projects" }}
 *   trail={[
 *     { label: "Projects", to: "/owner/projects" },
 *     { label: "Park Avenue Traditional" }, // last item is current page (no `to`)
 *   ]}
 * />
 */
export default function BackBar({ parent, trail = [], testId = "back-bar" }) {
  const navigate = useNavigate();
  const handleBack = (e) => {
    e?.preventDefault?.();
    if (getHistoryDepth() > 0) {
      // We have prior in-app history → real browser back.
      navigate(-1);
    } else if (parent?.to) {
      navigate(parent.to);
    } else {
      // No fallback declared and no history → noop (don't bounce out of SPA).
      window.scrollTo({ top: 0 });
    }
  };

  return (
    <nav
      aria-label="Breadcrumb"
      data-testid={testId}
      className="wn-backbar flex items-center flex-wrap"
      style={{
        gap: 14,
        marginBottom: 18,
        minHeight: 30,
      }}
    >
      <button
        type="button"
        onClick={handleBack}
        data-testid={`${testId}-back`}
        aria-label={`Back${parent?.label ? ` to ${parent.label}` : ""}`}
        title={
          getHistoryDepth() > 0
            ? "Back to the previous page"
            : parent?.label
              ? `Back to ${parent.label}`
              : "Back"
        }
        className="wn-backbar-btn inline-flex items-center"
        style={{
          gap: 6,
          padding: "5px 12px 5px 9px",
          borderRadius: 999,
          border: "1px solid #D9D8E4",
          background: "#FBFAFD",
          color: "#1B1F4B",
          fontFamily: '"Nunito Sans", sans-serif',
          fontWeight: 700,
          fontSize: 12,
          letterSpacing: "0.04em",
          cursor: "pointer",
          transition: "border-color 160ms ease, background-color 160ms ease",
          whiteSpace: "nowrap",
        }}
      >
        <ArrowLeft size={13} strokeWidth={2} />
        Back
      </button>

      {trail.length > 0 && (
        <ol
          className="flex items-center flex-wrap"
          style={{
            gap: 6,
            listStyle: "none",
            padding: 0,
            margin: 0,
            minWidth: 0,
          }}
        >
          {trail.map((seg, i) => {
            const isLast = i === trail.length - 1;
            return (
              <li
                key={`${seg.label}-${i}`}
                className="flex items-center"
                style={{
                  gap: 6,
                  minWidth: 0,
                  maxWidth: "100%",
                }}
              >
                {seg.to && !isLast ? (
                  <Link
                    to={seg.to}
                    data-testid={`${testId}-crumb-${i}`}
                    style={{
                      color: "#6C6B85",
                      fontFamily: '"Nunito Sans", sans-serif',
                      fontWeight: 600,
                      fontSize: 12.5,
                      letterSpacing: "0.02em",
                      textDecoration: "none",
                      whiteSpace: "nowrap",
                    }}
                    className="wn-backbar-crumb"
                  >
                    {seg.label}
                  </Link>
                ) : (
                  <span
                    data-testid={`${testId}-crumb-current`}
                    style={{
                      color: "#1B1F4B",
                      fontFamily: '"Nunito Sans", sans-serif',
                      fontWeight: 800,
                      fontSize: 12.5,
                      letterSpacing: "0.02em",
                      maxWidth: 360,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={seg.label}
                    aria-current={isLast ? "page" : undefined}
                  >
                    {seg.label}
                  </span>
                )}
                {!isLast && (
                  <ChevronRight
                    size={12}
                    color="#9AA0C0"
                    strokeWidth={1.8}
                    aria-hidden
                  />
                )}
              </li>
            );
          })}
        </ol>
      )}
    </nav>
  );
}
